/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowPathIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  XCircleIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  DockerContainerAction,
  DockerHost,
  DockerLiveContainer,
  getDockerHostContainers,
  getDockerLiveContainers,
  runDockerContainerAction,
  getDockerUpdates,
  updateDockerContainer,
} from "@/api/docker";
import { ArrowUpCircleIcon } from "@heroicons/react/24/outline";
import { showToast } from "@/components/common/Toast";
import { Bar, fmtPct } from "@/components/servers/kit";
import { ConfirmDialog, PortLinks, StateBadge, fmtDate, fmtSize, hostAddress } from "./shared";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

export type ContainerTab = "details" | "logs" | "stats";

interface Props {
  host: DockerHost;
  /** Pre-filter by compose project (set when coming from the Stacks page). */
  stack?: string;
  onClearStack: () => void;
  onOpen: (containerId: string, tab?: ContainerTab) => void;
}

const ACTIONS: { id: DockerContainerAction; label: string; icon: React.ComponentType<{ className?: string }>; needs: (c: DockerLiveContainer) => boolean; danger?: boolean }[] = [
  { id: "start", label: "Start", icon: PlayIcon, needs: (c) => c.state !== "running" && c.state !== "paused" },
  { id: "stop", label: "Stop", icon: StopIcon, needs: (c) => c.state === "running" || c.state === "restarting" },
  { id: "kill", label: "Kill", icon: XCircleIcon, needs: (c) => c.state === "running" || c.state === "paused" || c.state === "restarting", danger: true },
  { id: "restart", label: "Restart", icon: ArrowPathIcon, needs: (c) => c.state === "running" || c.state === "exited" },
  { id: "pause", label: "Pause", icon: PauseIcon, needs: (c) => c.state === "running" },
  { id: "unpause", label: "Resume", icon: PlayIcon, needs: (c) => c.state === "paused" },
  { id: "remove", label: "Remove", icon: TrashIcon, needs: (c) => c.state !== "running" && c.state !== "paused" && c.state !== "restarting", danger: true },
];

const STATE_FILTERS = ["running", "stopped", "paused", "failing"] as const;
type StateFilter = (typeof STATE_FILTERS)[number];

const matchesState = (c: DockerLiveContainer, f: StateFilter) =>
  f === "running" ? c.state === "running" : f === "paused" ? c.state === "paused" : f === "failing" ? c.state === "restarting" || c.state === "dead" : ["exited", "created"].includes(c.state);

export const ContainersView: React.FC<Props> = ({ host, stack, onClearStack, onOpen }) => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<StateFilter[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<DockerContainerAction | null>(null);

  const { data: live = [], isLoading, isError, error } = useQuery({
    queryKey: ["docker-live", host.id],
    queryFn: () => getDockerLiveContainers(host.id),
    refetchInterval: 10_000,
  });
  // CPU / memory come from the monitored (cached) rows.
  const { data: cached = [] } = useQuery({
    queryKey: ["docker-host-containers", host.id],
    queryFn: () => getDockerHostContainers(host.id),
    refetchInterval: 15_000,
    retry: false,
  });
  // Newer images in the registries: slow (it asks them), so it is read rarely.
  const { data: updates = [] } = useQuery({
    queryKey: ["docker-updates", host.id],
    queryFn: () => getDockerUpdates(host.id),
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: false,
  });
  const updatable = useMemo(() => new Set(updates.filter((u) => u.available).map((u) => u.containerId)), [updates]);
  const [updating, setUpdating] = useState<DockerLiveContainer | null>(null);
  const update = useMutation({
    mutationFn: (c: DockerLiveContainer) => updateDockerContainer(host.id, c.id),
    onSuccess: () => {
      showToast("Container updated", "success");
      setUpdating(null);
      queryClient.invalidateQueries({ queryKey: ["docker-live", host.id] });
      queryClient.invalidateQueries({ queryKey: ["docker-updates", host.id] });
    },
    onError: (e: Error) => {
      showToast("Update failed", "error", { description: e.message });
      setUpdating(null);
    },
  });
  const statsById = useMemo(() => new Map(cached.map((c) => [c.containerId, c.latest])), [cached]);

  const address = hostAddress(host);
  const q = query.trim().toLowerCase();
  const rows = useMemo(
    () =>
      live
        .filter((c) => !stack || c.project === stack)
        .filter((c) => filters.length === 0 || filters.some((f) => matchesState(c, f)))
        .filter((c) => !q || `${c.name} ${c.image} ${c.project ?? ""} ${c.service ?? ""} ${c.networks.map((n) => n.ip).join(" ")}`.toLowerCase().includes(q))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [live, stack, filters, q],
  );

  const chosen = rows.filter((c) => selected.has(c.id));
  const allOn = rows.length > 0 && rows.every((c) => selected.has(c.id));

  const run = useMutation({
    mutationFn: async ({ action, targets }: { action: DockerContainerAction; targets: DockerLiveContainer[] }) => {
      const results = await Promise.allSettled(targets.map((c) => runDockerContainerAction(host.id, c.id, action)));
      const failed = results.flatMap((r, i) => (r.status === "rejected" ? [`${targets[i].name}: ${(r.reason as Error).message}`] : []));
      return { action, ok: targets.length - failed.length, failed };
    },
    onSuccess: ({ action, ok, failed }) => {
      if (failed.length === 0) showToast(`${ok} container${ok === 1 ? "" : "s"}: ${action} done`, "success");
      else showToast(`${action}: ${failed.length} failed`, "error", { description: failed.slice(0, 3).join("\n") });
      setSelected(new Set());
      setConfirm(null);
      queryClient.invalidateQueries({ queryKey: ["docker-live", host.id] });
      queryClient.invalidateQueries({ queryKey: ["docker-host-containers", host.id] });
      queryClient.invalidateQueries({ queryKey: ["docker-info", host.id] });
    },
    onError: (e: Error) => {
      showToast("Action failed", "error", { description: e.message });
      setConfirm(null);
    },
  });

  const trigger = (action: DockerContainerAction) => {
    const targets = chosen.filter((c) => ACTIONS.find((a) => a.id === action)!.needs(c));
    if (targets.length === 0) return;
    if (action === "kill" || action === "remove") setConfirm(action);
    else run.mutate({ action, targets });
  };

  const toggleFilter = (f: StateFilter) => setFilters((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  const toggleRow = (id: string) =>
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const confirmTargets = confirm ? chosen.filter((c) => ACTIONS.find((a) => a.id === confirm)!.needs(c)) : [];

  return (
    <div className="dkr-stack">
      <div className="dkr-toolbar">
        <div className="dkr-actions" role="toolbar" aria-label="Container actions">
          {ACTIONS.map((a) => {
            const enabled = chosen.some((c) => a.needs(c));
            return (
              <button
                key={a.id}
                type="button"
                className={`dkr-act${a.danger ? " dkr-act--danger" : ""}`}
                disabled={!enabled || run.isPending}
                onClick={() => trigger(a.id)}
                title={enabled ? `${a.label} ${chosen.filter((c) => a.needs(c)).length} selected` : `${a.label} (select containers first)`}
              >
                <a.icon />
                {a.label}
              </button>
            );
          })}
        </div>
        <label className="rt-search dkr-search">
          <MagnifyingGlassIcon />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, image, stack, IP…" />
        </label>
      </div>

      <div className="rt-bar">
        {STATE_FILTERS.map((f) => (
          <button key={f} type="button" className="rt-chip" aria-pressed={filters.includes(f)} onClick={() => toggleFilter(f)}>
            {f === "stopped" ? "Stopped" : f === "failing" ? "Failing" : f === "paused" ? "Paused" : "Running"}
            <em>{live.filter((c) => matchesState(c, f)).length}</em>
          </button>
        ))}
        {stack && (
          <button type="button" className="rt-chip" aria-pressed onClick={onClearStack} title="Show all stacks">
            Stack: {stack} ✕
          </button>
        )}
        <span className="dkr-dim dkr-count">
          {`${rows.length} of ${live.length}${chosen.length > 0 ? ` · ${chosen.length} selected` : ""}`}
        </span>
      </div>

      <div className="dkr-tablewrap">
        <table className="dkr-table">
          <thead>
            <tr>
              <th className="dkr-chk">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allOn}
                  onChange={() => setSelected(allOn ? new Set() : new Set(rows.map((c) => c.id)))}
                />
              </th>
              <th>State</th>
              <th>Name</th>
              <th>Stack</th>
              <th>Image</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>IP address</th>
              <th>Published ports</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={11} className="dkr-empty">
                  Loading…
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={11} className="dkr-empty dkr-bad">
                  {(error as Error).message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="dkr-empty">
                  {live.length === 0 ? "No containers on this host." : "No containers match the filters."}
                </td>
              </tr>
            ) : (
              rows.map((c) => {
                const st = statsById.get(c.id);
                const memPct = st && st.memLimitBytes ? (st.memUsageBytes / st.memLimitBytes) * 100 : undefined;
                return (
                  <tr key={c.id} className={selected.has(c.id) ? "dkr-sel" : ""} onClick={() => onOpen(c.id)}>
                    <td className="dkr-chk" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${c.name}`} checked={selected.has(c.id)} onChange={() => toggleRow(c.id)} />
                    </td>
                    <td>
                      <StateBadge state={c.state} />
                    </td>
                    <td className="dkr-name" title={c.status}>
                      <b>
                        {c.name}
                        {updatable.has(c.id) && <span className="dkr-upd">update</span>}
                      </b>
                      <small>{c.status}</small>
                    </td>
                    <td>{c.project ? <span className="dkr-tag">{c.project}</span> : <span className="dkr-dim">—</span>}</td>
                    <td className="dkr-mono">
                      <span className="dkr-trunc" title={c.image}>
                        {c.image}
                      </span>
                    </td>
                    <td className="dkr-meter">
                      {c.state === "running" && st ? (
                        <>
                          <Bar pct={Math.min(100, st.cpuPercent)} warn={70} crit={90} />
                          <b><AnimatedNumber value={st.cpuPercent} mountFrom={null} format={(n) => fmtPct(n, n < 10 ? 1 : 0)} /></b>
                        </>
                      ) : (
                        <span className="dkr-dim">—</span>
                      )}
                    </td>
                    <td className="dkr-meter">
                      {c.state === "running" && st ? (
                        <>
                          <Bar pct={memPct ?? 0} warn={80} crit={92} />
                          <b>{fmtSize(st.memUsageBytes)}</b>
                        </>
                      ) : (
                        <span className="dkr-dim">—</span>
                      )}
                    </td>
                    <td className="dkr-mono">{c.networks.length ? c.networks.map((n) => n.ip).join(", ") : <span className="dkr-dim">—</span>}</td>
                    <td>
                      <PortLinks ports={c.ports} address={address} />
                    </td>
                    <td className="dkr-dim dkr-nowrap">{fmtDate(c.created)}</td>
                    <td className="dkr-quick" onClick={(e) => e.stopPropagation()}>
                      {updatable.has(c.id) && (
                        <button type="button" className="dkr-upd-btn" title="Update the image" aria-label={`Update ${c.name}`} onClick={() => setUpdating(c)}>
                          <ArrowUpCircleIcon />
                        </button>
                      )}
                      <button type="button" title="Logs" aria-label={`Logs of ${c.name}`} onClick={() => onOpen(c.id, "logs")}>
                        <DocumentTextIcon />
                      </button>
                      <button type="button" title="Stats" aria-label={`Stats of ${c.name}`} onClick={() => onOpen(c.id, "stats")}>
                        <ChartBarIcon />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={updating !== null}
        busy={update.isPending}
        title="Update container?"
        message={
          <>
            {`The newest image of ${updating?.image ?? ""} is pulled and `}
            <b>{updating?.name}</b>
            {" is recreated with the same settings. It restarts briefly; if the new one fails to start, the old one is put back."}
          </>
        }
        confirmLabel="Update"
        onClose={() => !update.isPending && setUpdating(null)}
        onConfirm={() => updating && update.mutate(updating)}
      />

      <ConfirmDialog
        open={confirm !== null}
        danger
        busy={run.isPending}
        title={confirm === "kill" ? "Kill containers?" : "Remove containers?"}
        message={
          <>
            {confirm === "kill" ? "SIGKILL will be sent immediately to " : "These containers will be deleted (volumes are kept): "}
            <b>{confirmTargets.map((c) => c.name).join(", ")}</b>
          </>
        }
        confirmLabel={confirm === "kill" ? "Kill" : "Remove"}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && run.mutate({ action: confirm, targets: confirmTargets })}
      />
    </div>
  );
};
